/**
 * Kiosku si lexues — punëtori mban QR Pass-in, kamera e lexon.
 *
 * Ekrani rri gjithmonë në skanim dhe kthehet vetë aty: askush nuk duhet të
 * shtypë asgjë për punëtorin e radhës. Rezultati zë tërë ekranin sepse lexohet
 * nga një metër larg, dhe ngjyra e mban peshën e mesazhit — jeshile filloi,
 * verdhë ishte tashmë aktive, kuqe s'u pranua.
 *
 * Përfundimi i punës nuk ndodh me të njëjtin skanim: kalohet në modin
 * "Përfundo punën" dhe pyetja "A je i sigurt?" del para se të shkruhet gjë.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Stack,
} from '@mui/material';
import CheckCircleRoundedIcon   from '@mui/icons-material/CheckCircleRounded';
import InfoRoundedIcon          from '@mui/icons-material/InfoRounded';
import ErrorRoundedIcon         from '@mui/icons-material/ErrorRounded';
import QrCodeScannerIcon        from '@mui/icons-material/QrCodeScanner';
import LoginIcon                from '@mui/icons-material/Login';
import LogoutIcon               from '@mui/icons-material/Logout';
import HelpOutlineIcon          from '@mui/icons-material/HelpOutline';
import VideocamOffIcon          from '@mui/icons-material/VideocamOff';

const API_URL = process.env.REACT_APP_API_URL || '';

const RESULT_MS  = 3000;  // sa qëndron konfirmimi para kthimit te skanimi
const ERROR_MS   = 4200;  // gabimi rri pak më gjatë — ka çka të lexohet
const CONFIRM_MS = 20000; // pyetja e përfundimit anulohet vetë nëse s'përgjigjet

// ── Sinjali zanor ────────────────────────────────────────────────────────────
// Ton i sintetizuar në vend të një skedari: kiosku shpesh punon pa internet dhe
// një asset që s'ngarkohet do të thotë skanim pa kthim përgjigjeje.
let audioCtx = null;
function tone(steps) {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume();

    steps.forEach(({ hz, at, dur, vol = 0.18 }) => {
      const osc  = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const t0 = audioCtx.currentTime + at;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(vol, t0 + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);
    });
  } catch { /* zëri është shtesë, jo kusht */ }
}

const SOUND = {
  success: () => tone([{ hz: 880, at: 0, dur: 0.13 }, { hz: 1320, at: 0.12, dur: 0.22 }]),
  notice:  () => tone([{ hz: 660, at: 0, dur: 0.22 }]),
  error:   () => tone([{ hz: 240, at: 0, dur: 0.18, vol: 0.22 },
                       { hz: 190, at: 0.18, dur: 0.26, vol: 0.22 }]),
};

// ── Pamja e rezultatit ───────────────────────────────────────────────────────
const PALETTE = {
  success: { bg: 'linear-gradient(160deg,#065f46 0%,#059669 55%,#10b981 100%)', Icon: CheckCircleRoundedIcon },
  notice:  { bg: 'linear-gradient(160deg,#78350f 0%,#b45309 55%,#f59e0b 100%)', Icon: InfoRoundedIcon },
  error:   { bg: 'linear-gradient(160deg,#7f1d1d 0%,#b91c1c 55%,#ef4444 100%)', Icon: ErrorRoundedIcon },
};

/** Përgjigjja e serverit → çka shfaqet në ekran. */
function toScreen(data, mode) {
  const code = data?.code;

  if (code === 'STARTED') {
    return {
      tone: 'success',
      title: data.message || 'Puna filloi me sukses',
      line1: `Mirë se erdhe, ${data.firstName || data.workerName}!`,
      line2: `Ora e fillimit: ${data.startedAtHHMM}`,
      hold:  RESULT_MS,
    };
  }

  if (code === 'ENDED') {
    const h = Math.floor((data.workMinutes || 0) / 60);
    const m = (data.workMinutes || 0) % 60;
    return {
      tone: 'success',
      title: 'Puna përfundoi',
      line1: `Faleminderit, ${data.firstName || data.workerName}!`,
      line2: `${data.startedAtHHMM} – ${data.endedAtHHMM} · ${h}h ${m}min`,
      hold:  RESULT_MS,
    };
  }

  if (code === 'ALREADY_ACTIVE') {
    return {
      tone: 'notice',
      title: 'Puna është tashmë aktive',
      line1: `Filluar në orën ${data.startedAtHHMM}`,
      line2: data.firstName ? `${data.firstName}, s'ke nevojë të skanosh sërish.` : '',
      hold:  RESULT_MS,
    };
  }

  const TITLES = {
    INVALID_QR:           'QR e panjohur',
    INVALID_OTP:          'QR e skaduar',
    REPLAY_DETECTED:      'QR e përdorur',
    RATE_LIMITED:         'Shumë tentativa',
    WORKER_NOT_FOUND:     'Punëtor i panjohur',
    WORKER_INACTIVE:      'Llogari joaktive',
    NOT_ENROLLED:         'QR Pass i paaktivizuar',
    LOCATION_NOT_ALLOWED: 'Lokacion i palejuar',
    OUTSIDE_SCHEDULE:     'Jashtë orarit',
    SESSION_FINISHED:     'Puna e sotme ka përfunduar',
    NO_ACTIVE_SESSION:    mode === 'end' ? 'S\'ka punë aktive' : 'S\'ka sesion',
    CONFIRM_EXPIRED:      'Konfirmimi skadoi',
    SERVER_ERROR:         'Gabim serveri',
    NETWORK:              'S\'ka lidhje me serverin',
  };

  return {
    tone: 'error',
    title: TITLES[code] || 'Nuk u pranua',
    line1: data?.message || 'Provo sërish ose kontakto HR-në.',
    line2: '',
    hold:  ERROR_MS,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
export default function KioskScanScreen({ kioskToken, kioskTenant, deviceId, onExit }) {
  const [mode,    setMode]    = useState('start');   // 'start' | 'end'
  const [phase,   setPhase]   = useState('booting'); // booting|scanning|busy|result|confirm|camera_error
  const [result,  setResult]  = useState(null);
  const [confirm, setConfirm] = useState(null);      // { workerName, startedAtHHMM, minutes, ticket }
  const [camError, setCamError] = useState('');

  const scannerRef = useRef(null);
  const lockRef    = useRef(false);   // kamera e lëshon kodin dhjetëra herë/sek
  const timerRef   = useRef(null);
  const modeRef    = useRef(mode);    // callback-u i skanerit ngrin state-in
  const aliveRef   = useRef(true);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  // ── Kthimi te skanimi ──────────────────────────────────────────────────────
  const backToScanning = useCallback(() => {
    clearTimer();
    setResult(null);
    setConfirm(null);
    if (!aliveRef.current) return;
    setPhase('scanning');
    lockRef.current = false;
    try { scannerRef.current?.resume(); } catch { /* skaneri s'ishte në pauzë */ }
  }, []);

  const showResult = useCallback((screen) => {
    setResult(screen);
    setPhase('result');
    SOUND[screen.tone]?.();
    clearTimer();
    timerRef.current = setTimeout(backToScanning, screen.hold);
  }, [backToScanning]);

  // ── Thirrja e serverit ─────────────────────────────────────────────────────
  const call = useCallback(async (path, body) => {
    const r = await fetch(`${API_URL}/api/qr/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Kiosk-Token': kioskToken,
        // Kompania e kioskut — pa të serveri e kërkon te kompania e parazgjedhur.
        ...(kioskTenant ? { 'X-Kiosk-Tenant': kioskTenant } : {}),
      },
      body: JSON.stringify({ device_id: parseInt(deviceId, 10), ...body }),
      signal: AbortSignal.timeout(12000),
    });
    return r.json();
  }, [kioskToken, kioskTenant, deviceId]);

  // ── Çka bëhet me një kod të lexuar ─────────────────────────────────────────
  const handleScan = useCallback(async (decodedText) => {
    if (lockRef.current) return;
    lockRef.current = true;
    try { scannerRef.current?.pause(true); } catch { /* s'ka rëndësi */ }

    setPhase('busy');

    const currentMode = modeRef.current;
    let data;
    try {
      data = currentMode === 'end'
        ? await call('kiosk/end-work',   { qr: decodedText })
        : await call('kiosk/start-work', { qr: decodedText });
    } catch {
      data = { code: 'NETWORK', message: 'Kiosku nuk po e arrin serverin. Provo sërish.' };
    }
    if (!aliveRef.current) return;

    // Përfundimi ndalon këtu dhe pyet — asgjë s'është shkruar ende.
    if (data?.code === 'CONFIRM_REQUIRED') {
      SOUND.notice();
      setConfirm({
        workerName:    data.workerName,
        firstName:     data.firstName,
        startedAtHHMM: data.startedAtHHMM,
        minutes:       data.workMinutesSoFar || 0,
        ticket:        data.confirmTicket,
      });
      setPhase('confirm');
      clearTimer();
      timerRef.current = setTimeout(backToScanning, CONFIRM_MS);
      return;
    }

    showResult(toScreen(data, currentMode));
  }, [call, showResult, backToScanning]);

  // ── Konfirmimi i përfundimit ───────────────────────────────────────────────
  const confirmEnd = useCallback(async () => {
    if (!confirm?.ticket) return;
    clearTimer();
    setPhase('busy');
    let data;
    try {
      data = await call('kiosk/end-work', { ticket: confirm.ticket });
    } catch {
      data = { code: 'NETWORK', message: 'Kiosku nuk po e arrin serverin. Provo sërish.' };
    }
    if (!aliveRef.current) return;
    setConfirm(null);
    setMode('start');            // pas përfundimit kiosku kthehet në gjendjen e paracaktuar
    showResult(toScreen(data, 'end'));
  }, [confirm, call, showResult]);

  // ── Kamera ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    aliveRef.current = true;
    let scanner;

    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e) {
        if (!aliveRef.current) return;
        setCamError(e?.name === 'NotAllowedError'
          ? 'Kamera është e bllokuar. Lejoje kamerën në cilësimet e shfletuesit.'
          : 'Nuk u gjet kamerë në këtë pajisje.');
        setPhase('camera_error');
        return;
      }

      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!aliveRef.current || !document.getElementById('kiosk-qr-reader')) return;

        scanner = new Html5Qrcode('kiosk-qr-reader', { verbose: false });
        scannerRef.current = scanner;

        const cfg = { fps: 12, qrbox: { width: 300, height: 300 }, aspectRatio: 1 };
        try {
          await scanner.start({ facingMode: 'environment' }, cfg, handleScan, () => {});
        } catch {
          await scanner.start({ facingMode: 'user' }, cfg, handleScan, () => {});
        }
        if (!aliveRef.current) { try { await scanner.stop(); } catch {} return; }
        setPhase('scanning');
      } catch {
        if (!aliveRef.current) return;
        setCamError('Skaneri nuk u nis. Rifresko faqen e kioskut.');
        setPhase('camera_error');
      }
    })();

    return () => {
      aliveRef.current = false;
      clearTimer();
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) { s.stop().then(() => s.clear()).catch(() => {}); }
    };
  }, [handleScan]);

  // ── Pamja ──────────────────────────────────────────────────────────────────
  const isEnd = mode === 'end';
  const accent = isEnd ? '#b91c1c' : '#0f766e';

  return (
    <Box sx={{
      position: 'relative', height: '100vh', overflow: 'hidden',
      bgcolor: '#0b1120', color: '#fff',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Koka ── */}
      <Box sx={{
        px: 4, py: 2.5, display: 'flex', alignItems: 'center', gap: 2,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <QrCodeScannerIcon sx={{ fontSize: 34, color: accent === '#0f766e' ? '#5eead4' : '#fca5a5' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, letterSpacing: -0.5 }}>
            {isEnd ? 'Përfundo punën' : 'Fillo punën me QR Code'}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)' }}>
            Mbaje QR Pass-in para kamerës
          </Typography>
        </Box>
        {onExit && (
          <Button onClick={onExit} sx={{ color: 'rgba(255,255,255,0.35)', textTransform: 'none' }}>
            Dil
          </Button>
        )}
      </Box>

      {/* ── Kamera ── */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          id="kiosk-qr-reader"
          sx={{
            width: 'min(56vh, 90vw)', height: 'min(56vh, 90vw)',
            borderRadius: 5, overflow: 'hidden',
            border: `4px solid ${accent}`,
            boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 24px 60px rgba(0,0,0,0.55)`,
            '& video': { objectFit: 'cover', width: '100% !important', height: '100% !important' },
          }}
        />

        {phase === 'booting' && (
          <Box sx={{ position: 'absolute', textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#5eead4' }} />
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.6)' }}>Duke ndezur kamerën…</Typography>
          </Box>
        )}

        {phase === 'camera_error' && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, p: 6,
            bgcolor: '#0b1120', textAlign: 'center',
          }}>
            <VideocamOffIcon sx={{ fontSize: 72, color: '#f87171' }} />
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 800 }}>Kamera nuk është gati</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 520 }}>{camError}</Typography>
          </Box>
        )}

        {/* Loading — kiosku duhet të tregojë se po punon, ndryshe skanohet dy herë */}
        {phase === 'busy' && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2.5,
            bgcolor: 'rgba(11,17,32,0.82)', backdropFilter: 'blur(3px)',
          }}>
            <CircularProgress size={64} thickness={4} sx={{ color: '#5eead4' }} />
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>Duke verifikuar…</Typography>
          </Box>
        )}
      </Box>

      {/* ── Zgjedhja e veprimit ── */}
      <Box sx={{
        px: 4, py: 3, display: 'flex', gap: 2, justifyContent: 'center',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        {[
          { key: 'start', label: 'Fillo punën',    Icon: LoginIcon,  color: '#0f766e', on: '#14b8a6' },
          { key: 'end',   label: 'Përfundo punën', Icon: LogoutIcon, color: '#b91c1c', on: '#ef4444' },
        ].map(({ key, label, Icon, color, on }) => (
          <Button
            key={key}
            onClick={() => { setMode(key); tone([]); backToScanning(); }}
            startIcon={<Icon />}
            sx={{
              minWidth: 240, py: 1.8, borderRadius: 3, fontSize: '1.05rem', fontWeight: 800,
              textTransform: 'none',
              color: '#fff',
              bgcolor: mode === key ? on : 'rgba(255,255,255,0.06)',
              border: `2px solid ${mode === key ? on : 'rgba(255,255,255,0.12)'}`,
              '&:hover': { bgcolor: mode === key ? color : 'rgba(255,255,255,0.12)' },
            }}
          >
            {label}
          </Button>
        ))}
      </Box>

      {/* ── Rezultati: mbulon tërë ekranin ── */}
      {phase === 'result' && result && (() => {
        const { bg, Icon } = PALETTE[result.tone];
        return (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 20,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, px: 6, textAlign: 'center', background: bg,
            animation: 'wipeIn 0.28s cubic-bezier(.2,.8,.2,1)',
            '@keyframes wipeIn': { from: { opacity: 0, transform: 'scale(0.97)' }, to: { opacity: 1, transform: 'scale(1)' } },
          }}>
            <Icon sx={{
              fontSize: 128, mb: 1,
              animation: 'pop 0.45s cubic-bezier(.2,1.4,.4,1)',
              '@keyframes pop': { from: { transform: 'scale(0.4)', opacity: 0 }, to: { transform: 'scale(1)', opacity: 1 } },
            }} />
            <Typography sx={{ fontSize: 'clamp(2rem, 5vw, 3.4rem)', fontWeight: 900, letterSpacing: -1.2, lineHeight: 1.1 }}>
              {result.title}
            </Typography>
            {result.line1 && (
              <Typography sx={{ fontSize: 'clamp(1.2rem, 2.6vw, 2rem)', fontWeight: 600, opacity: 0.95 }}>
                {result.line1}
              </Typography>
            )}
            {result.line2 && (
              <Typography sx={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)', opacity: 0.8, fontVariantNumeric: 'tabular-nums' }}>
                {result.line2}
              </Typography>
            )}
            <Box sx={{
              mt: 3, height: 5, width: 'min(420px, 70vw)', borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.25)', overflow: 'hidden',
            }}>
              <Box sx={{
                height: '100%', bgcolor: 'rgba(255,255,255,0.9)',
                animation: `drain ${result.hold}ms linear forwards`,
                '@keyframes drain': { from: { width: '100%' }, to: { width: '0%' } },
              }} />
            </Box>
          </Box>
        );
      })()}

      {/* ── Pyetja e përfundimit ── */}
      {phase === 'confirm' && confirm && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 3, px: 6, textAlign: 'center',
          background: 'linear-gradient(160deg,#0c4a6e 0%,#155e75 60%,#0e7490 100%)',
        }}>
          <HelpOutlineIcon sx={{ fontSize: 104 }} />
          <Typography sx={{ fontSize: 'clamp(1.8rem, 4.4vw, 3rem)', fontWeight: 900, letterSpacing: -1 }}>
            Përfundo punën?
          </Typography>
          <Stack spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 'clamp(1.1rem, 2.4vw, 1.7rem)', fontWeight: 600 }}>
              {confirm.workerName}
            </Typography>
            <Chip
              label={`Filluar në orën ${confirm.startedAtHHMM} · ${Math.floor(confirm.minutes / 60)}h ${confirm.minutes % 60}min`}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: '1rem', py: 2.4, px: 1 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              onClick={backToScanning}
              sx={{
                minWidth: 200, py: 1.8, borderRadius: 3, fontSize: '1.05rem', fontWeight: 800,
                textTransform: 'none', color: '#fff',
                border: '2px solid rgba(255,255,255,0.35)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Jo, anulo
            </Button>
            <Button
              onClick={confirmEnd}
              sx={{
                minWidth: 240, py: 1.8, borderRadius: 3, fontSize: '1.05rem', fontWeight: 900,
                textTransform: 'none', color: '#0c4a6e', bgcolor: '#fff',
                '&:hover': { bgcolor: '#e2e8f0' },
              }}
            >
              Po, përfundo
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
